import { useEffect, useState } from 'react';
import type { ColumnResizedEvent, GridApi } from 'ag-grid-community';
import { computeDetailPanelLeftOffset } from '../masterDetail/computeDetailPanelLeftOffset';

/**
 * Tracks left padding so detail panels align with a master-column anchor field.
 * Returns 0 (or fallback) when anchor tracking is disabled / api not ready.
 */
export const useDetailPanelLeftOffset = ({
  masterGridApi,
  detailPanelAnchorField,
  detailPanelLeftOffsetFallback = 0,
}: {
  masterGridApi: GridApi | null;
  detailPanelAnchorField?: string;
  detailPanelLeftOffsetFallback?: number;
}) => {
  const [detailPanelLeftOffset, setDetailPanelLeftOffset] = useState(
    detailPanelLeftOffsetFallback
  );

  useEffect(() => {
    if (!detailPanelAnchorField || !masterGridApi) {
      return undefined;
    }

    const syncLeftOffset = () => {
      setDetailPanelLeftOffset(
        computeDetailPanelLeftOffset(
          masterGridApi,
          detailPanelAnchorField,
          detailPanelLeftOffsetFallback
        )
      );
    };

    const syncLeftOffsetOnResizeFinished = (event: ColumnResizedEvent) => {
      if (event.finished) {
        syncLeftOffset();
      }
    };

    syncLeftOffset();
    masterGridApi.addEventListener('columnResized', syncLeftOffsetOnResizeFinished);
    masterGridApi.addEventListener('columnVisible', syncLeftOffset);
    masterGridApi.addEventListener('columnMoved', syncLeftOffset);
    masterGridApi.addEventListener('gridColumnsChanged', syncLeftOffset);

    return () => {
      masterGridApi.removeEventListener('columnResized', syncLeftOffsetOnResizeFinished);
      masterGridApi.removeEventListener('columnVisible', syncLeftOffset);
      masterGridApi.removeEventListener('columnMoved', syncLeftOffset);
      masterGridApi.removeEventListener('gridColumnsChanged', syncLeftOffset);
    };
  }, [detailPanelAnchorField, detailPanelLeftOffsetFallback, masterGridApi]);

  useEffect(() => {
    if (!detailPanelAnchorField || !masterGridApi) {
      return;
    }
    masterGridApi.redrawRows?.();
  }, [detailPanelAnchorField, detailPanelLeftOffset, masterGridApi]);

  return detailPanelLeftOffset;
};
